import LoggerService from '../LoggerService';

// reducer actions
// On a chargé les données dans un Context API depuis App
import {REDUCER_ACTIONS} from '../../contexts/global';

import {setAccessToken,setRefreshToken} from '../../helpers/localStorageHelper';

import {postRefreshToken} from './ApiEndpoints';
import {_checkErrors} from './services/_base';
import I_LOGIN from './interfaces/I_LOGIN';
import APP_CONFIG from 'config';

const LOCALSTORAGE:boolean = APP_CONFIG.LOCALSTORAGE_ACTIVE === 'true';
const LOCALSTORAGE_PREFIX:string = APP_CONFIG.LOCALSTORAGE_PREFIX;

/**
 * Class singleton permettant de centraliser les appels de refresh token
 * @folder Services
 * @class
 */
class RefreshTokenSingleton {
  private static _instance: RefreshTokenSingleton;

  refreshing?: boolean;  // true si une requete refresh est en cours
  refreshResponse?: Response;  // reponse async de la requete refresh
  pendingRefreshAskers?: number;  // nombre d'actions qui attendent le refresh
  refreshResponseAccessToken?: string|any;  // accessToken renvoyé par le refresh en cours, ou undefined si on ne l'a pas encore recu

  logger: any;


  /**
   * Renvoie l'instance du service
   * @returns {RefreshTokenSingleton} singleton instance
   */
  static getInstance() {
    if (!RefreshTokenSingleton._instance) {
      RefreshTokenSingleton._instance = new RefreshTokenSingleton();
    }
    return RefreshTokenSingleton._instance;
  }

  private constructor() {
    this.logger = LoggerService.getInstance();
    this.init();
  }

  init() {
    this.refreshing = false;
    this.refreshResponse = undefined;
    this.pendingRefreshAskers = undefined;
    this.logger.debug('RefreshTokenSingleton - init');
  }

  /**
   * Requete l'API pour de nouveaux token, puis les stocke dans le context et le localStorage,
   * et renvoie le nouveau access token
   * @param param0
   * @returns
   */
  async waitForRefresh({refreshToken,dispatch}:{refreshToken:string,dispatch:any}):Promise<string> {
    this.logger.debug('RefreshTokenSingleton - waitForRefresh - wait for token refresh...');

    // on incremente le nombre de demandeurs
    this.pendingRefreshAskers = this.pendingRefreshAskers === undefined ? 1 : this.pendingRefreshAskers + 1;
    this.logger.debug('RefreshTokenSingleton - waitForRefresh - '+this.pendingRefreshAskers);

    // s'il n'y a pas de refresh en cours
    if (!this.refreshing) {
      this.refreshResponse = undefined;
      this.refreshing = true;
      this.logger.debug('RefreshTokenSingleton - waitForRefresh - no current refresh action, sending new refresh request...');
      // requete le endpoint
      this.refreshResponse = await postRefreshToken({refreshToken});
      this.logger.debug('RefreshTokenSingleton - waitForRefresh - returned response after await',this.refreshResponse);

      // verifie que la reponse est ok, sinon throw error
      try {
        await _checkErrors(this.refreshResponse); // là ca pète
      }
      catch(err){
        this.refreshResponseAccessToken = err;

        // on decremente le nombre de demandeurs
        if (this.pendingRefreshAskers !== undefined) {
          this.pendingRefreshAskers--;
        }
        this._resetRefresher();
        return Promise.reject(err);
      }

      // extrait les données (meme format que login)
      const body:I_LOGIN = await this.refreshResponse.json();
      this.logger.debug('RefreshTokenSingleton - waitForRefresh - ok, returned body',body);

      const newAccessToken = body.access_token;
      const newRefreshToken = body.refresh_token;

      this._dispatchAndStore({accessToken:newAccessToken,refreshToken:newRefreshToken,dispatch});

      // on la stocke pour les autres demandes en parallele
      this.refreshResponseAccessToken = newAccessToken;

      // on decremente le nombre de demandeurs
      if (this.pendingRefreshAskers !== undefined) {
        this.pendingRefreshAskers--;
      }
      this._resetRefresher();

      return newAccessToken;
    }
    // s'il y a deja un refresh en cours
    else {
      this.logger.debug('RefreshTokenSingleton - waitForRefresh - refresh action already sent, waiting...');

      // on va boucler un certain temps pour checker si refreshResponseAccessToken a une valeur
      try{
        const newAccessToken = await this._checkRefreshResponseAccessToken(1,4);

        // on decremente le nombre de demandeurs
        if (this.pendingRefreshAskers !== undefined) {
          this.pendingRefreshAskers--;
        }
        this._resetRefresher();

        return newAccessToken;
      }
      catch(err){
        this.refreshResponseAccessToken = err;

        // on decremente le nombre de demandeurs
        if (this.pendingRefreshAskers !== undefined) {
          this.pendingRefreshAskers--;
        }
        this._resetRefresher();
        return Promise.reject(err);
      }
    }
  }

  async _checkRefreshResponseAccessToken(hit:number,hitMax:number):Promise<string> {
    this.logger.debug('RefreshTokenSingleton - _checkRefreshResponseAccessToken - ',{hit,hitMax,refreshResponseAccessToken:this.refreshResponseAccessToken});
    if (hit > hitMax) {
      return Promise.reject({
        error: true,
        errorCode: 1401,
        errorText: '',
        errorMessage: `Parallel refresh request, but max hits reached.`
      });
    }
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (this.refreshResponseAccessToken !== undefined) {
          if (typeof this.refreshResponseAccessToken !== 'string' && this.refreshResponse !== undefined) {
            // la requete refresh a planté, on a transmis l'objet erreur
            this.logger.debug('RefreshTokenSingleton - _checkRefreshResponseAccessToken - ',{result:this.refreshResponseAccessToken,response:this.refreshResponse});
            // _checkErrors(this.refreshResponse); // là ca pète
            reject(this.refreshResponseAccessToken);
          }
          else {
            resolve(this.refreshResponseAccessToken);
          }
        }
        else {
          resolve(this._checkRefreshResponseAccessToken(hit + 1,hitMax));
        }
      },500);
    });

  }

  _dispatchAndStore({accessToken,refreshToken,dispatch}:{accessToken:string,refreshToken:string,dispatch:any}) {
    // on stocke les tokens dans le context et le localStorage
    // a priori, il n'y a qu'ici qu'on fait ca dans un service
    if (accessToken) {
      dispatch({type:REDUCER_ACTIONS.SET_ACCESS_TOKEN,payload:{token:accessToken}});
      if (LOCALSTORAGE) {
        setAccessToken(LOCALSTORAGE_PREFIX || '',accessToken);
        this.logger.trace('RefreshTokenSingleton - waitForRefresh - Stored access token in localStorage.');
      }
    }
    if (refreshToken) {
      dispatch({type:REDUCER_ACTIONS.SET_REFRESH_TOKEN,payload:{token:refreshToken}});
      if (LOCALSTORAGE) {
        setRefreshToken(LOCALSTORAGE_PREFIX || '',refreshToken);
        this.logger.trace('RefreshTokenSingleton - waitForRefresh - Stored refresh token in localStorage.');
      }
    }
  }

  _resetRefresher() {
    // quand tous les listeners ont eu la reponse, on la reset
    // this.logger.trace('RefreshTokenSingleton - waitForRefresh',{pendingRefreshAskers:this.pendingRefreshAskers});
    if (this.pendingRefreshAskers === 0) {
      this.logger.trace('RefreshTokenSingleton - _resetRefresher - reset all');
      this.refreshResponse = undefined;
      this.refreshing = false;
      this.pendingRefreshAskers = undefined;
      this.refreshResponseAccessToken = undefined;
    }
  }
}
export default RefreshTokenSingleton;
